import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx-js-style';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileSpreadsheet,
  PackageCheck,
  Search,
  Truck,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { logisticsOrdersAPI, productsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const DAY = 24 * 60 * 60 * 1000;
const STATUSES = [
  ['pending', 'Pending'],
  ['confirmed', 'Confirmed'],
  ['in_transit', 'In Transit'],
  ['delayed', 'Delayed'],
  ['delivered', 'Delivered'],
  ['cancelled', 'Cancelled'],
];
const FOLLOW_UPS = [
  ['not_started', 'Not Started'],
  ['seller_contacted', 'Seller Contacted'],
  ['awaiting_confirmation', 'Awaiting Confirmation'],
  ['resolved', 'Resolved'],
];
const STATUS_OPTIONS = [{ value: 'all', label: 'All Statuses' }, ...STATUSES.map(([value, label]) => ({ value, label }))];
const FALLBACK_SELLERS = ['Prime Route Logistics', 'FreshCart Supply', 'Metro Cold Chain'];
const SEARCH_DEBOUNCE_MS = 800;
const today = () => new Date().toISOString().split('T')[0];
const shiftDate = (value, days) => {
  const date = new Date(`${value || today()}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};
const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const getTimestamp = (value, endOfDay = false) => {
  if (!value) return Number.NaN;
  return new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}`).getTime();
};
const statusLookup = Object.fromEntries(STATUSES);
const followUpLookup = Object.fromEntries(FOLLOW_UPS);
const CREATE_MODE_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'unplanned', label: 'Unplanned' },
];
const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  ...CREATE_MODE_OPTIONS,
];
const currentEta = (order) => order.revisedDelivery || order.expectedDelivery;
const isClosedOrder = (order) => ['delivered', 'cancelled'].includes(order.status);
const remaining = (value) => {
  if (!value) return 'No ETA';
  const diff = Math.ceil((getTimestamp(value) - Date.now()) / DAY);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Due tomorrow';
  return `${diff}d remaining`;
};
const statusLabel = (value) => statusLookup[value] || value;
const followUpLabel = (value) => followUpLookup[value] || value;
const orderTypeLabel = (value) => (String(value || 'planned').toLowerCase() === 'unplanned' ? 'Unplanned' : 'Planned');
const orderTypeBadgeClass = (value) => (String(value || 'planned').toLowerCase() === 'unplanned' ? 'badge-warning' : 'badge-info');
const badgeClass = (value) => ({
  pending: 'badge-info',
  confirmed: 'badge-info',
  in_transit: 'badge-warning',
  delayed: 'badge-danger',
  delivered: 'badge-success',
  cancelled: 'badge-danger',
}[value] || 'badge-info');
const progressValue = (order) => {
  if (order.status === 'delivered') return 100;
  if (order.status === 'cancelled') return 0;

  const start = getTimestamp(order.orderedDate);
  const end = getTimestamp(currentEta(order));
  const now = Date.now();

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  if (now <= start) return 0;
  if (now >= end) return 90;

  return Math.min(90, Math.max(0, Math.round(((now - start) / (end - start)) * 90)));
};
const progressGradient = (progress) => {
  const capped = Math.max(0, Math.min(90, progress));
  const endHue = capped <= 55
    ? (capped / 55) * 60
    : 60 + ((capped - 55) / 35) * 60;
  const middleHue = endHue * 0.55;

  return `linear-gradient(90deg, hsl(3 84% 59%) 0%, hsl(${Math.max(8, middleHue)} 86% 56%) 58%, hsl(${Math.max(12, endHue)} 74% 47%) 100%)`;
};
const normalizeImportKey = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
const normalizeImportValue = (value) => String(value ?? '').trim();
const normalizeImportQuantity = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : null;
};
const buildImportedSummary = (lineItems = []) => {
  if (!lineItems.length) return '';
  if (lineItems.length === 1) {
    const item = lineItems[0];
    return `${item.product || item.sku || 'Imported item'}${Number.isFinite(item.orderQuantity) ? ` x ${item.orderQuantity}` : ''}`;
  }

  const preview = lineItems
    .map((item) => item.product || item.sku)
    .filter(Boolean)
    .slice(0, 2)
    .join(', ');

  return `${lineItems.length} items imported${preview ? `: ${preview}${lineItems.length > 2 ? ', ...' : ''}` : ''}`;
};
const buildLineItemMergeKey = (item = {}) => {
  const sku = normalizeImportValue(item.sku || item.SKU).trim().toLowerCase();
  if (sku) return `sku::${sku}`;

  const productId = normalizeImportValue(item.productId || item.product_id).trim().toLowerCase();
  if (productId) return `product-id::${productId}`;

  const product = normalizeImportValue(item.product || item.Product || item.productName || item.name).trim().toLowerCase();
  if (product) return `product::${product}`;

  return normalizeImportValue(item.id).trim().toLowerCase();
};
const combineQuantityValues = (currentValue, nextValue, { preserveBlank = false } = {}) => {
  const numericValues = [currentValue, nextValue]
    .filter((value) => value !== '' && value !== null && value !== undefined)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (!numericValues.length) {
    return preserveBlank ? '' : 0;
  }

  return numericValues.reduce((sum, value) => sum + value, 0);
};
const aggregateLineItemsBySku = (items = []) => {
  const groups = new Map();

  items.forEach((item, index) => {
    const key = buildLineItemMergeKey(item) || `row-${index + 1}`;
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        ...item,
        id: item.id || `${key}-${index + 1}`,
      });
      return;
    }

    groups.set(key, {
      ...existing,
      id: existing.id || item.id || `${key}-${index + 1}`,
      sku: existing.sku || item.sku || '',
      product: existing.product || item.product || '',
      productId: existing.productId || item.productId || '',
      barcode: existing.barcode || item.barcode || '',
      orderQuantity: combineQuantityValues(existing.orderQuantity, item.orderQuantity),
      deliveredQuantity: combineQuantityValues(existing.deliveredQuantity, item.deliveredQuantity),
      receivedQuantity: combineQuantityValues(existing.receivedQuantity, item.receivedQuantity, { preserveBlank: true }),
      receivedUpdatedAt: item.receivedUpdatedAt || existing.receivedUpdatedAt || null,
      receivedUpdatedById: item.receivedUpdatedById || existing.receivedUpdatedById || null,
      receivedUpdatedByName: item.receivedUpdatedByName || existing.receivedUpdatedByName || null,
    });
  });

  return Array.from(groups.values()).map((item, index) => ({
    ...item,
    id: item.id || `${buildLineItemMergeKey(item) || 'line'}-${index + 1}`,
  }));
};
const mapImportedRows = (rows = []) => rows
  .map((row, index) => ({
    id: `import-${index + 1}`,
    sku: normalizeImportValue(getWorkbookValue(row, ['SKU'])),
    product: normalizeImportValue(getWorkbookValue(row, ['Product', 'Product Name'])),
    orderQuantity: normalizeImportQuantity(getWorkbookValue(row, ['Order Quantity', 'Order Qty', 'Ordered Quantity', 'Ordered Qty', 'Quantity', 'Qty'])),
  }))
  .filter((item) => item.sku || item.product || Number.isFinite(item.orderQuantity))
  .reduce((accumulator, item) => aggregateLineItemsBySku([...accumulator, item]), []);
const mapDeliveredRows = (rows = []) => rows
  .map((row, index) => ({
    id: `delivered-${index + 1}`,
    sku: normalizeImportValue(getWorkbookValue(row, ['SKU'])),
    product: normalizeImportValue(getWorkbookValue(row, ['Product', 'Product Name'])),
    deliveredQuantity: normalizeImportQuantity(getWorkbookValue(row, ['Delivered Quantity', 'Delivered Qty', 'Received Quantity', 'Received Qty', 'Quantity', 'Qty'])),
  }))
  .filter((item) => item.sku || item.product || Number.isFinite(item.deliveredQuantity))
  .reduce((accumulator, item) => aggregateLineItemsBySku([...accumulator, item]), []);
const getWorkbookValue = (row = {}, aliases = []) => {
  const lookup = new Map(
    Object.entries(row).map(([key, value]) => [normalizeImportKey(key), value])
  );

  for (const alias of aliases) {
    const value = lookup.get(normalizeImportKey(alias));
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }

  return '';
};
const normalizeWorkbookDate = (value) => {
  if (value === undefined || value === null || value === '') return '';

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }

  const text = normalizeImportValue(value);
  if (!text) return '';

  const isoMatch = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const dmyMatch = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const parsedDate = new Date(text);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().split('T')[0];
  }

  return '';
};
const normalizeStoreLookupValue = (value) => String(value ?? '').trim().toLowerCase();
const resolveWorkbookStore = (stores = [], rawStoreValue = '') => {
  const lookupValue = normalizeStoreLookupValue(rawStoreValue);
  if (!lookupValue) return null;

  return stores.find((store) => {
    const storeCandidates = [store.id, store.name, store.code];
    return storeCandidates.some((candidate) => normalizeStoreLookupValue(candidate) === lookupValue);
  }) || null;
};
const buildUnplannedDeliveredGroups = (rows = [], stores = [], defaults = {}) => {
  if (!stores.length) {
    throw new Error('Store list is still loading. Please try again in a moment.');
  }

  const groups = new Map();
  const defaultStore = resolveWorkbookStore(stores, defaults.storeId || defaults.storeName || defaults.storeCode || '');
  const defaultSeller = normalizeImportValue(defaults.seller);
  const defaultOrderedDate = normalizeWorkbookDate(defaults.orderedDate) || today();
  const defaultDeliveredOn = normalizeWorkbookDate(
    defaults.deliveredOn || defaults.deliveredAt || defaults.expectedDelivery
  ) || defaultOrderedDate;

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const storeLabel = getWorkbookValue(row, ['Store', 'Store Name', 'Store Code']) || defaultStore?.name || defaultStore?.code || '';
    const store = resolveWorkbookStore(stores, storeLabel) || defaultStore;
    const seller = normalizeImportValue(getWorkbookValue(row, ['Seller', 'Seller Name'])) || defaultSeller;
    const orderedDate = normalizeWorkbookDate(getWorkbookValue(row, ['Ordered Date', 'Order Date'])) || defaultOrderedDate;
    const deliveredOn = normalizeWorkbookDate(getWorkbookValue(row, ['Delivered On', 'Delivered Date'])) || defaultDeliveredOn;
    const sku = normalizeImportValue(getWorkbookValue(row, ['SKU']));
    const product = normalizeImportValue(getWorkbookValue(row, ['Product', 'Product Name']));
    const orderedQuantity = normalizeImportQuantity(getWorkbookValue(row, ['Order Quantity', 'Order Qty', 'Ordered Quantity', 'Ordered Qty', 'Quantity', 'Qty']));
    const deliveredQuantity = normalizeImportQuantity(getWorkbookValue(row, ['Delivered Quantity', 'Delivered Qty', 'Received Quantity', 'Received Qty', 'Quantity', 'Qty'])) ?? orderedQuantity;
    const finalOrderedQuantity = orderedQuantity ?? deliveredQuantity;

    if (!store) {
      throw new Error(`Store is required for the unplanned delivery upload on row ${rowNumber}.`);
    }
    if (!seller) {
      throw new Error(`Seller is required for the unplanned delivery upload on row ${rowNumber}.`);
    }
    if (!orderedDate) {
      throw new Error(`Ordered Date is required for the unplanned delivery upload on row ${rowNumber}.`);
    }
    if (!deliveredOn) {
      throw new Error(`Delivered On is required for the unplanned delivery upload on row ${rowNumber}.`);
    }
    if (!sku && !product) {
      throw new Error(`SKU or Product is required on row ${rowNumber}.`);
    }
    if (finalOrderedQuantity === null || finalOrderedQuantity === undefined) {
      throw new Error(`Order Qty or Delivered Qty is required on row ${rowNumber}.`);
    }

    const groupKey = [
      normalizeStoreLookupValue(store.id),
      normalizeStoreLookupValue(seller),
      orderedDate,
      deliveredOn,
    ].join('::');

    const existingGroup = groups.get(groupKey) || {
      storeId: String(store.id),
      storeName: store.name,
      seller,
      itemSummary: '',
      orderedDate,
      expectedDelivery: deliveredOn,
      deliveredAt: deliveredOn,
      type: 'unplanned',
      status: 'delivered',
      followUpStatus: 'resolved',
      note: '',
      changeMeta: {},
      deliveryControl: {
        deliveredLocked: true,
        unlockRequested: false,
        unlockRequestedAt: null,
        unlockRequestedById: null,
        unlockRequestedByName: null,
        unlockedAt: null,
        unlockedById: null,
        unlockedByName: null,
      },
      lineItems: [],
      goodsReceivedStatus: 'pending',
      goodsReceivedUpdatedAt: null,
      goodsReceivedUpdatedById: null,
      goodsReceivedUpdatedByName: null,
    };

    existingGroup.lineItems.push({
      id: `unplanned-${index + 1}-${existingGroup.lineItems.length + 1}`,
      sku,
      product,
      orderQuantity: finalOrderedQuantity,
      deliveredQuantity: deliveredQuantity ?? finalOrderedQuantity,
      receivedQuantity: '',
    });

    groups.set(groupKey, existingGroup);
  });

  return Array.from(groups.values()).map((group) => {
    const aggregatedLineItems = aggregateLineItemsBySku(group.lineItems).map((item, index) => ({
      ...item,
      id: item.id || `unplanned-line-${index + 1}`,
    }));

    return {
      ...group,
      lineItems: aggregatedLineItems,
      itemSummary: buildImportedSummary(aggregatedLineItems),
    };
  });
};
const lineItemKey = (item = {}) => buildLineItemMergeKey(item);
const mergeDeliveredLineItems = (existingLineItems = [], deliveredLineItems = []) => {
  const normalizedExisting = aggregateLineItemsBySku(existingLineItems);
  const normalizedDelivered = aggregateLineItemsBySku(deliveredLineItems);
  const deliveredMap = new Map(normalizedDelivered.map((item) => [lineItemKey(item), item]));
  const seen = new Set();

  const mergedExisting = normalizedExisting.map((item, index) => {
    const key = lineItemKey(item);
    seen.add(key);
    const deliveredItem = deliveredMap.get(key);

    return {
      ...item,
      id: item.id || `line-${index + 1}`,
      deliveredQuantity: deliveredItem ? deliveredItem.deliveredQuantity : 0,
    };
  });

  const deliveredOnly = normalizedDelivered
    .filter((item) => !seen.has(lineItemKey(item)))
    .map((item, index) => ({
      id: item.id || `extra-delivered-${index + 1}`,
      sku: item.sku,
      product: item.product,
      orderQuantity: 0,
      deliveredQuantity: item.deliveredQuantity,
      receivedQuantity: '',
      receivedUpdatedAt: null,
      receivedUpdatedById: null,
      receivedUpdatedByName: null,
    }));

  return [...mergedExisting, ...deliveredOnly];
};
const mergeOrderedLineItems = (existingLineItems = [], orderedLineItems = []) => {
  const normalizedExisting = aggregateLineItemsBySku(existingLineItems);
  const normalizedOrdered = aggregateLineItemsBySku(orderedLineItems);
  const existingMap = new Map(normalizedExisting.map((item) => [lineItemKey(item), item]));
  const orderedMap = new Map(normalizedOrdered.map((item) => [lineItemKey(item), item]));
  const keys = new Set([...existingMap.keys(), ...orderedMap.keys()]);

  return Array.from(keys).map((key, index) => {
    const existing = existingMap.get(key) || {};
    const ordered = orderedMap.get(key) || {};

    return {
      id: existing.id || ordered.id || `ordered-${index + 1}`,
      sku: ordered.sku || existing.sku || '',
      product: ordered.product || existing.product || '',
      orderQuantity: ordered.orderQuantity ?? existing.orderQuantity ?? 0,
      deliveredQuantity: existing.deliveredQuantity ?? 0,
      receivedQuantity: existing.receivedQuantity ?? '',
      receivedUpdatedAt: existing.receivedUpdatedAt ?? null,
      receivedUpdatedById: existing.receivedUpdatedById ?? null,
      receivedUpdatedByName: existing.receivedUpdatedByName ?? null,
    };
  });
};
const hasQuantityValue = (value) => value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value));
const getQuantityNumber = (value) => (hasQuantityValue(value) ? Number(value) : 0);
const buildLineItemComparison = (lineItems = []) => {
  const rows = (lineItems || []).map((item, index) => {
    const ordered = getQuantityNumber(item.orderQuantity);
    const delivered = getQuantityNumber(item.deliveredQuantity);
    const hasOrdered = hasQuantityValue(item.orderQuantity);
    const hasDelivered = hasQuantityValue(item.deliveredQuantity);
    const difference = delivered - ordered;
    const isMissingDelivered = hasOrdered && !hasDelivered;
    const isExtraDelivered = !hasOrdered && hasDelivered;
    const isQuantityMismatch = hasOrdered && hasDelivered && difference !== 0;
    const isMismatch = isMissingDelivered || isExtraDelivered || isQuantityMismatch;

    let status = 'Matched';
    if (isMissingDelivered) status = 'Not delivered';
    else if (isExtraDelivered) status = 'Extra delivered';
    else if (isQuantityMismatch) status = difference > 0 ? 'Over delivered' : 'Short delivered';

    return {
      ...item,
      _comparisonKey: item.id || `${item.sku || item.product || 'item'}-${index}`,
      ordered,
      delivered,
      difference,
      status,
      isMismatch,
    };
  });

  return {
    rows,
    mismatchRows: rows.filter((item) => item.isMismatch),
    orderedTotal: rows.reduce((sum, item) => sum + item.ordered, 0),
    deliveredTotal: rows.reduce((sum, item) => sum + item.delivered, 0),
  };
};
const getMismatchCount = (order) => buildLineItemComparison(order?.lineItems || []).mismatchRows.length;
const hasDeliveredLineData = (order) => (order?.lineItems || []).some((item) => hasQuantityValue(item.deliveredQuantity));
const readWorkbookRows = async (file, requiredHeaders = []) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

  if (!rows.length) {
    throw new Error('The uploaded file is empty.');
  }

  if (!requiredHeaders.length) {
    return rows;
  }

  const headers = Object.keys(rows[0] || {});
  const normalizedHeaders = headers.map((header) => normalizeImportKey(header));
  const hasRequiredHeaders = requiredHeaders.every((header) => normalizedHeaders.includes(normalizeImportKey(header)));

  if (!hasRequiredHeaders) {
    throw new Error(`Use these exact headers: ${requiredHeaders.join(', ')}.`);
  }

  return rows.map((row) => Object.fromEntries(
    requiredHeaders.map((header) => {
      const sourceIndex = normalizedHeaders.indexOf(header.toLowerCase());
      return [header, row[headers[sourceIndex]]];
    })
  ));
};
const renderChangedBy = (meta) => {
  if (!meta?.byUserName) return null;
  return (
    <div className="mt-2 text-xs text-gray-500">
      changed by {meta.byUserName}
    </div>
  );
};
const getDeliveryControl = (order) => order?.deliveryControl || {};
const isDeliveredLocked = (order) => order?.status === 'delivered' && !!getDeliveryControl(order).deliveredLocked;

const OrderSheetModal = ({ order, onClose, onSave, onReupload, onDeliveredUpload, canEdit }) => {
  const [items, setItems] = useState(() =>
    (order?.lineItems || []).map((item, index) => ({
      ...item,
      _key: item.id || `row-${index}`,
    }))
  );
  const [showMismatchesOnly, setShowMismatchesOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const deliveredFileInputRef = useRef(null);

  const comparison = useMemo(() => buildLineItemComparison(items), [items]);
  const displayRows = showMismatchesOnly ? comparison.mismatchRows : comparison.rows;
  const deliveredDataAvailable = Boolean(order?.deliveredSheetFileName)
    || comparison.rows.some((item) => hasQuantityValue(item.deliveredQuantity));

  const updateItem = (key, field, value) => {
    setItems((current) =>
      current.map((item) =>
        item._key === key ? { ...item, [field]: field.includes('Quantity') ? (value === '' ? null : Number(value)) : value } : item
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = items.map(({ _key, ...rest }) => rest);
      await onSave(order.id, cleaned);
      toast.success('Line items saved');
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReupload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      await onReupload(order, { target: { files: [file], value: '' } });
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to reupload');
    } finally {
      setSaving(false);
      event.target.value = '';
    }
  };

  const handleDeliveredReupload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !onDeliveredUpload) return;
    setSaving(true);
    try {
      await onDeliveredUpload(order, { target: { files: [file], value: '' } });
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to upload delivered file');
    } finally {
      setSaving(false);
      event.target.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-[999] bg-slate-950/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="absolute right-0 top-0 flex h-full w-full max-w-6xl flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl sm:w-[92vw] lg:w-[76vw]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Order Sheet - {order?.orderNumber || 'Unnamed'}
            </h2>
            {order?.orderedSheetFileName ? (
              <p className="mt-0.5 text-xs text-slate-500">{order.orderedSheetFileName}</p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Ordered File</p>
              <p className="mt-2 truncate text-sm font-semibold text-slate-900">{order?.orderedSheetFileName || 'Not uploaded'}</p>
              {order?.orderedSheetUploadedAt ? (
                <p className="mt-1 text-xs text-slate-500">{formatDate(order.orderedSheetUploadedAt)}</p>
              ) : null}
            </div>
            <div className="rounded-xl border border-success-200 bg-success-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-success-700">Delivered File</p>
              <p className="mt-2 truncate text-sm font-semibold text-success-950">
                {order?.deliveredSheetFileName || (deliveredDataAvailable ? 'Delivered quantities loaded' : 'Not uploaded')}
              </p>
              {order?.deliveredSheetUploadedAt ? (
                <p className="mt-1 text-xs text-success-700">{formatDate(order.deliveredSheetUploadedAt)}</p>
              ) : null}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Quantity</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">
                Ordered {comparison.orderedTotal} / Delivered {comparison.deliveredTotal}
              </p>
              <p className="mt-1 text-xs text-slate-500">{comparison.rows.length} product rows</p>
            </div>
            <button
              type="button"
              className={`rounded-xl border p-4 text-left transition ${
                comparison.mismatchRows.length
                  ? 'border-danger-200 bg-danger-50 hover:bg-danger-100'
                  : 'border-success-200 bg-success-50 hover:bg-success-100'
              }`}
              onClick={() => setShowMismatchesOnly((current) => !current)}
            >
              <p className={`text-xs font-semibold uppercase tracking-wide ${comparison.mismatchRows.length ? 'text-danger-700' : 'text-success-700'}`}>
                Mismatch Counter
              </p>
              <p className={`mt-2 text-3xl font-bold ${comparison.mismatchRows.length ? 'text-danger-700' : 'text-success-700'}`}>
                {comparison.mismatchRows.length}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {showMismatchesOnly ? 'Showing differences only' : 'Click to filter differences'}
              </p>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {items.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">No line items in this order.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3 font-semibold">#</th>
                  <th className="px-3 py-3 font-semibold">SKU</th>
                  <th className="px-3 py-3 font-semibold">Product</th>
                  <th className="px-3 py-3 font-semibold text-right">Order Qty</th>
                  <th className="px-3 py-3 font-semibold text-right">Delivered Qty</th>
                  <th className="px-3 py-3 font-semibold text-right">Difference</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayRows.map((item, index) => (
                  <tr key={item._comparisonKey} className={item.isMismatch ? 'bg-danger-50/50' : 'hover:bg-slate-50'}>
                    <td className="px-3 py-3 text-slate-400">{index + 1}</td>
                    <td className="px-3 py-3">
                      {canEdit ? (
                        <input className="input w-full text-sm" value={item.sku || ''} onChange={(event) => updateItem(item._key, 'sku', event.target.value)} />
                      ) : (
                        <span className="font-medium text-slate-800">{item.sku || '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {canEdit ? (
                        <input className="input w-full text-sm" value={item.product || ''} onChange={(event) => updateItem(item._key, 'product', event.target.value)} />
                      ) : (
                        <span className="text-slate-700">{item.product || '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {canEdit ? (
                        <input className="input w-[90px] text-right text-sm font-mono tabular-nums" type="number" min="0" value={item.orderQuantity ?? ''} onChange={(event) => updateItem(item._key, 'orderQuantity', event.target.value)} />
                      ) : (
                        <span className="font-mono tabular-nums text-slate-700">{hasQuantityValue(item.orderQuantity) ? item.orderQuantity : '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-mono tabular-nums text-slate-700">{hasQuantityValue(item.deliveredQuantity) ? item.deliveredQuantity : '-'}</span>
                    </td>
                    <td className={`px-3 py-3 text-right font-mono tabular-nums ${item.difference < 0 ? 'text-danger-700' : item.difference > 0 ? 'text-warning-700' : 'text-slate-500'}`}>
                      {item.difference > 0 ? `+${item.difference}` : item.difference}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`badge ${item.isMismatch ? 'badge-danger' : 'badge-success'}`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {displayRows.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">
                No differences found for this shipment.
              </div>
            ) : null}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            {canEdit ? (
              <>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-700 transition hover:bg-amber-100">
                  <Upload className="h-4 w-4" />
                  <span>Reupload Ordered File</span>
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" disabled={saving} onChange={handleReupload} />
                </label>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-primary-300 bg-primary-50 px-4 py-2 text-xs font-medium text-primary-700 transition hover:bg-primary-100">
                  <Upload className="h-4 w-4" />
                  <span>{order?.deliveredSheetFileName ? 'Replace Delivered File' : 'Upload Delivered File'}</span>
                  <input ref={deliveredFileInputRef} type="file" accept=".xlsx,.xls" className="hidden" disabled={saving} onChange={handleDeliveredReupload} />
                </label>
              </>
            ) : null}
          </div>
          <div className="flex items-center justify-end gap-3">
            <button type="button" className="btn-secondary text-sm" onClick={onClose} disabled={saving}>Close</button>
            {canEdit ? (
              <button type="button" className="btn-primary text-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const AssistantManagerTasks = ({ viewMode = 'desk' }) => {
  const { isAdmin, user, hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const selectedStoreContext = useMemo(() => ({
    storeInfoId: selectedStore?.storeInfoId || selectedStore?.store_info_id || null,
    vendorId: selectedStore?.vendorId || selectedStore?.vendor_id || null,
    legacyStoreId: selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || null,
    storeName: selectedStore?.name || selectedStore?.storeName || selectedStore?.storeCode || '',
    storeCode: selectedStore?.storeCode || selectedStore?.code || '',
  }), [selectedStore]);
  const selectedStoreId = String(selectedStoreContext.storeInfoId || selectedStoreContext.legacyStoreId || '').trim();
  const selectedStoreName = selectedStoreContext.storeName || 'Selected Store';
  const hasStoreContext = Boolean(selectedStoreId);
  const selectedStoreRecords = useMemo(() => (
    selectedStoreId
      ? [{
          id: selectedStoreId,
          name: selectedStoreName,
          code: selectedStoreContext.storeCode,
        }]
      : []
  ), [selectedStoreContext.storeCode, selectedStoreId, selectedStoreName]);
  const isTrackerOnlyView = viewMode === 'tracker';
  const canViewLogisticDesk = isTrackerOnlyView
    ? hasFeatureAccess('logistics.tracker')
    : (hasFeatureAccess('logistics.manage') || hasFeatureAccess('logistics.tracker'));
  const canManageLogisticsOrders = !isTrackerOnlyView && hasFeatureAccess('logistics.manage');
  const stores = selectedStoreRecords;
  const [orders, setOrders] = useState([]);
  const [sellerOptions, setSellerOptions] = useState(FALLBACK_SELLERS);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [savingOrderId, setSavingOrderId] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [fromDate, setFromDate] = useState(shiftDate(today(), -30));
  const [toDate, setToDate] = useState(today());
  const [orderMode, setOrderMode] = useState('planned');
  const [form, setForm] = useState({
    storeId: selectedStoreId || '',
    seller: '',
    itemSummary: '',
    orderedDate: today(),
    expectedDelivery: shiftDate(today(), 2),
    deliveredOn: today(),
    lineItems: [],
    uploadName: '',
  });
  const isPlannedMode = orderMode === 'planned';
  const [sheetModalOrder, setSheetModalOrder] = useState(null);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      storeId: selectedStoreId || '',
      seller: current.storeId === selectedStoreId ? current.seller : '',
    }));
  }, [selectedStoreId]);

  useEffect(() => {
    if (!canManageLogisticsOrders || !hasStoreContext) {
      setSellerOptions(FALLBACK_SELLERS);
      return;
    }

    const loadSellers = async () => {
      try {
        const response = await productsAPI.getSellers({ storeId: selectedStoreId });
        const sellers = response.data?.data || [];
        setSellerOptions(sellers.length ? sellers : FALLBACK_SELLERS);
      } catch (error) {
        setSellerOptions(FALLBACK_SELLERS);
        if (error?.response?.status && error.response.status !== 401) {
          toast.error(error?.response?.data?.message || 'Failed to load seller names');
        }
      }
    };

    loadSellers();
  }, [canManageLogisticsOrders, hasStoreContext, selectedStoreId]);

  useEffect(() => {
    if (!canViewLogisticDesk || !hasStoreContext) {
      setOrders([]);
      return;
    }

    const loadOrders = async () => {
      setLoadingOrders(true);
      try {
        const params = {
          status: statusFilter,
          type: typeFilter,
          search: debouncedSearch,
          fromDate,
          toDate,
          storeId: selectedStoreId,
        };

        const response = await logisticsOrdersAPI.getAll(params);
        setOrders(response.data?.data || []);
        setDataSource(response.data?.meta?.source || '');
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load logistics orders');
      } finally {
        setLoadingOrders(false);
      }
    };

    loadOrders();
  }, [canViewLogisticDesk, debouncedSearch, fromDate, hasStoreContext, selectedStoreId, statusFilter, toDate, typeFilter]);

  const dueSoonOrders = useMemo(
    () =>
      orders.filter((order) => {
        if (isClosedOrder(order)) return false;
        const diff = Math.ceil((getTimestamp(currentEta(order)) - Date.now()) / DAY);
        return diff >= 0 && diff <= 1;
      }),
    [orders]
  );

  const riskOrders = useMemo(
    () =>
      orders.filter((order) => {
        if (isClosedOrder(order)) return false;
        return order.status === 'delayed' || getTimestamp(currentEta(order), true) < Date.now();
      }),
    [orders]
  );

  const stats = useMemo(
    () => ({
      active: orders.filter((order) => !isClosedOrder(order)).length,
      delayed: riskOrders.length,
      dueSoon: dueSoonOrders.length,
      delivered: orders.filter((order) => order.status === 'delivered' && order.deliveredAt).length,
    }),
    [dueSoonOrders.length, orders, riskOrders.length]
  );

  const replaceOrder = (nextOrder) => {
    setOrders((current) => current.map((order) => (order.id === nextOrder.id ? nextOrder : order)));
  };

  const updateLocalOrder = (id, patch) => {
    setOrders((current) => current.map((order) => (order.id === id ? { ...order, ...patch } : order)));
  };

  const persistOrder = async (order) => {
    setSavingOrderId(order.id);
    try {
      const response = await logisticsOrdersAPI.update(order.id, order);
      replaceOrder(response.data?.data || order);
      setDataSource(response.data?.meta?.source || dataSource);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save logistics order');
    } finally {
      setSavingOrderId('');
    }
  };

  const persistLatestOrder = async (orderId, overrides = {}) => {
    const currentOrder = orders.find((entry) => entry.id === orderId);
    if (!currentOrder) return;
    await persistOrder({ ...currentOrder, ...overrides });
  };

  const runOrderAction = async (orderId, request, successMessage, fallbackErrorMessage) => {
    setSavingOrderId(orderId);
    try {
      const response = await request();
      replaceOrder(response.data?.data || orders.find((entry) => entry.id === orderId));
      setDataSource(response.data?.meta?.source || dataSource);
      if (successMessage) {
        toast.success(successMessage);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || fallbackErrorMessage);
    } finally {
      setSavingOrderId('');
    }
  };

  const handleStatusChange = (order, status) => {
    const deliveryControl = status === 'delivered'
      ? {
          ...(order.deliveryControl || {}),
          deliveredLocked: true,
          unlockRequested: false,
          unlockRequestedAt: null,
          unlockRequestedById: null,
          unlockRequestedByName: null,
          unlockedAt: null,
          unlockedById: null,
          unlockedByName: null,
        }
      : (order.status === 'delivered'
        ? {
            deliveredLocked: false,
            unlockRequested: false,
            unlockRequestedAt: null,
            unlockRequestedById: null,
            unlockRequestedByName: null,
            unlockedAt: null,
            unlockedById: null,
            unlockedByName: null,
          }
        : order.deliveryControl);

    const nextOrder = {
      ...order,
      status,
      deliveredAt: status === 'delivered'
        ? (order.deliveredAt || new Date().toISOString())
        : (order.status === 'delivered' ? null : order.deliveredAt),
      deliveryControl,
    };

    if (status !== 'delayed' && nextOrder.followUpStatus === 'resolved') {
      nextOrder.followUpStatus = 'not_started';
    }

    updateLocalOrder(order.id, nextOrder);
    persistOrder(nextOrder);
  };

  const handleFollowUpChange = (order, followUpStatus) => {
    const nextOrder = { ...order, followUpStatus };
    updateLocalOrder(order.id, { followUpStatus });
    persistOrder(nextOrder);
  };

  const handleRevisedEtaChange = (order, revisedDelivery) => {
    updateLocalOrder(order.id, { revisedDelivery });
  };

  const handleRevisedEtaSave = (order) => {
    persistLatestOrder(order.id);
  };

  const handleNoteChange = (order, note) => {
    updateLocalOrder(order.id, { note });
  };

  const handleNoteSave = (order) => {
    persistLatestOrder(order.id);
  };

  const handleUnlockRequest = (order) => {
    runOrderAction(
      order.id,
      () => logisticsOrdersAPI.requestUnlock(order.id),
      'Unlock request sent to admin',
      'Failed to request unlock'
    );
  };

  const handleUnlockApproval = (order) => {
    runOrderAction(
      order.id,
      () => logisticsOrdersAPI.approveUnlock(order.id),
      'Delivered status unlocked',
      'Failed to approve unlock'
    );
  };

  const handleOrderFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const lineItems = mapImportedRows(await readWorkbookRows(file));
      if (!lineItems.length) {
        throw new Error('No valid SKU rows were found in the uploaded file.');
      }

      setForm((current) => ({
        ...current,
        lineItems,
        uploadName: file.name,
        itemSummary: current.itemSummary.trim() || buildImportedSummary(lineItems),
      }));
      toast.success(`${lineItems.length} order lines imported`);
    } catch (error) {
      toast.error(error.message || 'Failed to read the uploaded Excel file');
    } finally {
      event.target.value = '';
    }
  };

  const handleUnplannedDeliveredFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!form.storeId) {
      toast.error('Select a store from the header before uploading delivered orders');
      event.target.value = '';
      return;
    }

    if (!form.seller) {
      toast.error('Seller is required for unplanned uploads');
      event.target.value = '';
      return;
    }

    setSavingOrderId('unplanned-upload');
    try {
      const rows = await readWorkbookRows(file);
        const groupedOrders = buildUnplannedDeliveredGroups(rows, stores, {
          storeId: form.storeId,
          seller: form.seller,
          orderedDate: form.orderedDate || today(),
          deliveredOn: form.deliveredOn || form.orderedDate || today(),
        });

      if (!groupedOrders.length) {
        throw new Error('No valid unplanned delivered order rows were found in the uploaded file.');
      }

      const createdOrders = [];
      for (const group of groupedOrders) {
        const response = await logisticsOrdersAPI.create({
          ...group,
          deliveredSheetFileName: file.name,
          deliveredSheetUploadedAt: new Date().toISOString(),
          deliveredSheetUploadedById: user?.id || null,
          deliveredSheetUploadedByName: user?.name || null,
        });
        if (response.data?.data) {
          createdOrders.push(response.data.data);
        }
      }

      if (!createdOrders.length) {
        throw new Error('The unplanned delivered orders could not be created.');
      }

      setOrders((current) => [...createdOrders, ...current]);
      setDataSource('logistics_orders');
      toast.success(`${createdOrders.length} unplanned delivered order${createdOrders.length === 1 ? '' : 's'} imported`);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to upload unplanned delivered orders');
    } finally {
      setSavingOrderId('');
      event.target.value = '';
    }
  };

  const handleDeliveredFileUpload = async (order, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSavingOrderId(order.id);
    try {
      const deliveredLineItems = mapDeliveredRows(await readWorkbookRows(file));
      if (!deliveredLineItems.length) {
        throw new Error('No valid delivered quantity rows were found in the uploaded file.');
      }

      const mergedLineItems = mergeDeliveredLineItems(order.lineItems || [], deliveredLineItems);
      const response = await logisticsOrdersAPI.update(order.id, {
        ...order,
        lineItems: mergedLineItems,
        deliveredSheetFileName: file.name,
        deliveredSheetUploadedAt: new Date().toISOString(),
      });

      replaceOrder(response.data?.data || {
        ...order,
        lineItems: mergedLineItems,
        deliveredSheetFileName: file.name,
        deliveredSheetUploadedAt: new Date().toISOString(),
      });
      setDataSource(response.data?.meta?.source || dataSource);
      toast.success('Delivered file uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to upload delivered file');
    } finally {
      setSavingOrderId('');
      event.target.value = '';
    }
  };

  const handleOrderedFileUploadForOrder = async (order, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSavingOrderId(order.id);
    try {
      const orderedLineItems = mapImportedRows(await readWorkbookRows(file));
      if (!orderedLineItems.length) {
        throw new Error('No valid order quantity rows were found in the uploaded file.');
      }

      const mergedLineItems = mergeOrderedLineItems(order.lineItems || [], orderedLineItems);
      const response = await logisticsOrdersAPI.update(order.id, {
        ...order,
        itemSummary: order.itemSummary || buildImportedSummary(mergedLineItems),
        lineItems: mergedLineItems,
        orderedSheetFileName: file.name,
        orderedSheetUploadedAt: new Date().toISOString(),
      });

      replaceOrder(response.data?.data || {
        ...order,
        itemSummary: order.itemSummary || buildImportedSummary(mergedLineItems),
        lineItems: mergedLineItems,
      });
      setDataSource(response.data?.meta?.source || dataSource);
      toast.success('Order sheet uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to upload order sheet');
    } finally {
      setSavingOrderId('');
      event.target.value = '';
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Delete logistics order ${order.orderNumber || ''}? This cannot be undone.`)) return;

    setSavingOrderId(order.id);
    try {
      await logisticsOrdersAPI.delete(order.id);
      setOrders((current) => current.filter((entry) => entry.id !== order.id));
      toast.success('Logistics order deleted');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete logistics order');
    } finally {
      setSavingOrderId('');
    }
  };

  const clearImportedFile = () => {
    setForm((current) => ({
      ...current,
      lineItems: [],
      uploadName: '',
      itemSummary: current.itemSummary === buildImportedSummary(current.lineItems) ? '' : current.itemSummary,
    }));
  };

  const createOrder = async () => {
    if (!form.storeId) {
      toast.error('Select a store from the header before creating logistics orders');
      return;
    }

    if (!form.seller || !form.orderedDate || !form.expectedDelivery || (!form.itemSummary.trim() && !form.lineItems.length)) {
      toast.error('Seller, ordered date, ETA, and either item summary or uploaded order lines are required');
      return;
    }

    const store = stores.find((entry) => String(entry.id) === String(form.storeId));
    if (!store) {
      toast.error('Select a valid store from the header');
      return;
    }

    setSavingOrderId('create');
    try {
      const response = await logisticsOrdersAPI.create({
        storeId: store.id,
        storeName: store.name,
        seller: form.seller,
        type: 'planned',
        itemSummary: form.itemSummary.trim(),
        orderedDate: form.orderedDate,
        expectedDelivery: form.expectedDelivery,
        lineItems: form.lineItems,
        orderedSheetFileName: form.uploadName || null,
        orderedSheetUploadedAt: form.uploadName ? new Date().toISOString() : null,
      });

      const created = response.data?.data;
      setOrders((current) => [created, ...current]);
      setDataSource(response.data?.meta?.source || dataSource);
      setForm((current) => ({
        ...current,
        seller: '',
        itemSummary: '',
        orderedDate: today(),
        expectedDelivery: shiftDate(today(), 2),
        lineItems: [],
        uploadName: '',
      }));
      toast.success('Logistics order created');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create logistics order');
    } finally {
      setSavingOrderId('');
    }
  };

  const exportExcel = () => {
    downloadExcelWorkbook({
      fileName: `logistic-desk-${today()}.xlsx`,
      sheetName: 'Logistics Desk',
      columns: [
        { header: 'Order', key: 'orderNumber', width: 18 },
        { header: 'Type', key: 'type', width: 12, value: (order) => orderTypeLabel(order.type) },
        { header: 'Store', key: 'storeName', width: 22 },
        { header: 'Seller', key: 'seller', width: 22 },
        { header: 'Ordered Date', key: 'orderedDate', width: 14 },
        { header: 'Initial ETA', key: 'expectedDelivery', width: 14 },
        { header: 'Delivered On', key: 'deliveredAt', width: 18 },
        { header: 'Status', key: 'status', width: 14, value: (order) => statusLabel(order.status) },
        { header: 'Follow Up', key: 'followUpStatus', width: 18, value: (order) => followUpLabel(order.followUpStatus) },
        { header: 'Revised ETA', key: 'revisedEta', width: 14, value: (order) => currentEta(order) },
        { header: 'Note', key: 'note', width: 28 },
      ],
      rows: orders,
    });
  };

  if (!canViewLogisticDesk) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Logistics Tracker</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Logistics Tracker</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track shipments, update ETAs, and manage seller follow-ups across logistics operations.
          </p>
        </div>

        <ExportButton onClick={exportExcel} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="card border-primary-200 bg-primary-50">
          <div className="flex items-center gap-3">
            <Truck className="h-6 w-6 text-primary-600" />
            <div>
              <p className="text-sm font-medium text-primary-800">Active Orders</p>
              <p className="text-2xl font-bold text-primary-700">{stats.active}</p>
            </div>
          </div>
        </div>
        <div className="card border-warning-200 bg-warning-50">
          <div className="flex items-center gap-3">
            <Clock3 className="h-6 w-6 text-warning-600" />
            <div>
              <p className="text-sm font-medium text-warning-800">Due Today / Tomorrow</p>
              <p className="text-2xl font-bold text-warning-700">{stats.dueSoon}</p>
            </div>
          </div>
        </div>
        <div className="card border-danger-200 bg-danger-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-danger-600" />
            <div>
              <p className="text-sm font-medium text-danger-800">Delayed / Overdue</p>
              <p className="text-2xl font-bold text-danger-700">{stats.delayed}</p>
            </div>
          </div>
        </div>
        <div className="card border-success-200 bg-success-50">
          <div className="flex items-center gap-3">
            <PackageCheck className="h-6 w-6 text-success-600" />
            <div>
              <p className="text-sm font-medium text-success-800">Delivered</p>
              <p className="text-2xl font-bold text-success-700">{stats.delivered}</p>
            </div>
          </div>
        </div>
      </div>

      {canManageLogisticsOrders ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-6">
            <div className="card">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Create Logistics Order</h2>
                  <p className="text-sm text-gray-500">
                    {isPlannedMode
                      ? 'Register a new inbound shipment and set its first fixed ETA.'
                      : 'Import already delivered shipments directly into goods received.'}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="inline-flex rounded-lg bg-slate-100 p-1">
                    {CREATE_MODE_OPTIONS.map((option) => {
                      const isActive = orderMode === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setOrderMode(option.value)}
                          className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                            isActive
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className={`badge ${isPlannedMode ? 'badge-info' : 'badge-warning'}`}>
                    {isPlannedMode ? 'Planned' : 'Unplanned'}
                  </div>
                </div>
              </div>

              {isPlannedMode ? (
                <>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <FilterSelect
                      label="Seller"
                      value={form.seller}
                      options={[
                        { value: '', label: 'Select seller...' },
                        ...sellerOptions.map((seller) => ({ value: seller, label: seller })),
                      ]}
                      onChange={(value) => setForm((current) => ({ ...current, seller: value }))}
                    />
                    <div className="lg:col-span-2">
                      <label className="label">Item Summary</label>
                      <input
                        className="input"
                        type="text"
                        placeholder="Example: Frozen snacks restock + beverage refill"
                        value={form.itemSummary}
                        onChange={(event) => setForm((current) => ({ ...current, itemSummary: event.target.value }))}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Keep this as a short shipment summary. If you upload an order sheet, it can auto-fill from the imported rows.
                      </p>
                    </div>
                    <div className="lg:col-span-2">
                      <label className="label">Order Sheet Upload</label>
                      <label className="flex cursor-pointer flex-col gap-3 rounded-lg border border-dashed border-primary-200 bg-primary-50 px-4 py-4 text-sm text-primary-900 transition hover:border-primary-300 hover:bg-primary-100">
                        <div className="flex items-center gap-3">
                          <FileSpreadsheet className="h-5 w-5 text-primary-600" />
                          <div>
                            <p className="font-medium">Upload Excel file</p>
                            <p className="text-xs text-primary-700">Headers: SKU, Product, Qty, Quantity, Order Qty, or Order Quantity</p>
                          </div>
                        </div>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          onChange={handleOrderFileUpload}
                        />
                        <span className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-700 shadow-sm">
                          Choose file
                        </span>
                      </label>

                      {form.uploadName ? (
                        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{form.uploadName}</p>
                              <p className="mt-1 text-xs text-gray-500">
                                {form.lineItems.length} product rows ready for this logistics order.
                              </p>
                            </div>
                            <button type="button" className="text-gray-400 transition hover:text-danger-600" onClick={clearImportedFile}>
                              <XCircle className="h-5 w-5" />
                            </button>
                          </div>

                          <div className="mt-3 table-scroll-shell-compact">
                            <table className="min-w-full text-left text-xs text-gray-600">
                              <thead className="border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-400">
                                <tr>
                                  <th className="py-2 pr-3">SKU</th>
                                  <th className="py-2 pr-3">Product</th>
                                  <th className="py-2">Order Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {form.lineItems.slice(0, 4).map((item) => (
                                  <tr key={item.id} className="border-b border-gray-50">
                                    <td className="py-2 pr-3 font-medium text-gray-800">{item.sku || '-'}</td>
                                    <td className="py-2 pr-3">{item.product || '-'}</td>
                                    <td className="py-2">{item.orderQuantity ?? '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {form.lineItems.length > 4 ? (
                            <p className="mt-2 text-xs text-gray-400">
                              Showing 4 of {form.lineItems.length} imported rows.
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <label className="label">Ordered Date</label>
                      <input className="input" type="date" value={form.orderedDate} max={today()} onChange={(event) => setForm((current) => ({ ...current, orderedDate: event.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Initial ETA</label>
                      <input className="input" type="date" value={form.expectedDelivery} onChange={(event) => setForm((current) => ({ ...current, expectedDelivery: event.target.value }))} />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button type="button" className="btn-primary" onClick={createOrder} disabled={savingOrderId === 'create'}>
                      Create Logistics Order
                    </button>
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-warning-200 bg-warning-50/60 p-4">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <FilterSelect
                      label="Seller"
                      value={form.seller}
                      options={[
                        { value: '', label: 'Select seller...' },
                        ...sellerOptions.map((seller) => ({ value: seller, label: seller })),
                      ]}
                      onChange={(value) => setForm((current) => ({ ...current, seller: value }))}
                    />
                    <div>
                      <label className="label">Ordered Date</label>
                      <input
                        className="input"
                        type="date"
                        value={form.orderedDate}
                        max={today()}
                        onChange={(event) => setForm((current) => ({ ...current, orderedDate: event.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="label">Delivered On</label>
                      <input
                        className="input"
                        type="date"
                        value={form.deliveredOn}
                        max={today()}
                        onChange={(event) => setForm((current) => ({ ...current, deliveredOn: event.target.value }))}
                      />
                    </div>
                    <div className="lg:col-span-2">
                      <label className="label">Delivered Sheet Upload</label>
                      <label className="flex cursor-pointer flex-col gap-3 rounded-lg border border-dashed border-warning-200 bg-white px-4 py-4 text-sm text-warning-900 transition hover:border-warning-300 hover:bg-warning-50">
                        <div className="flex items-center gap-3">
                          <FileSpreadsheet className="h-5 w-5 text-warning-600" />
                          <div>
                            <p className="font-medium">Upload Excel file</p>
                            <p className="text-xs text-warning-700">
                              Headers: SKU, Product, Qty, Quantity, Order Qty, or Order Quantity
                            </p>
                          </div>
                        </div>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          disabled={savingOrderId === 'unplanned-upload'}
                          onChange={handleUnplannedDeliveredFileUpload}
                        />
                        <span className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-warning-700 shadow-sm">
                          Choose file
                        </span>
                      </label>
                        <p className="mt-2 text-xs text-gray-500">
                          Pick the seller above. The sheet only needs SKU, Product, and Qty or Quantity rows; the dates
                          default from the form if left unchanged.
                        </p>
                      </div>
                    </div>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="mb-4 flex items-center gap-3">
              <CalendarClock className="h-5 w-5 text-danger-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Action Alerts</h2>
                <p className="text-sm text-gray-500">Today and tomorrow ETA follow-up plus overdue shipments.</p>
              </div>
            </div>

            {dueSoonOrders.length === 0 && riskOrders.length === 0 ? (
              <p className="text-sm text-gray-500">No urgent logistics follow-up right now.</p>
            ) : (
              <div className="space-y-3">
                {dueSoonOrders.map((order) => (
                  <div key={`due-${order.id}`} className="rounded-xl border border-warning-200 bg-warning-50 p-4">
                    <p className="font-medium text-warning-900">{order.orderNumber}</p>
                    <p className="mt-1 text-sm text-warning-800">
                      ETA is {remaining(currentEta(order))}. Contact {order.seller} for final confirmation.
                    </p>
                  </div>
                ))}
                {riskOrders.map((order) => (
                  <div key={`risk-${order.id}`} className="rounded-xl border border-danger-200 bg-danger-50 p-4">
                    <p className="font-medium text-danger-900">{order.orderNumber}</p>
                    <p className="mt-1 text-sm text-danger-800">
                      {order.seller} needs escalation for {order.itemSummary}.
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="card min-h-[760px]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Live Order Tracker</h2>
            </div>
            {loadingOrders ? <p className="text-sm text-gray-400">Refreshing orders...</p> : null}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr_0.8fr]">
            <label className="relative block">
              <span className="label">Search</span>
              <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
              <input
                className="input pl-10"
                placeholder="Order, seller, item..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>

            <FilterSelect label="Status" value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />

            <FilterSelect label="Type" value={typeFilter} options={TYPE_FILTER_OPTIONS} onChange={setTypeFilter} />

            <div>
              <label className="label">From Date</label>
              <input
                className="input"
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </div>

            <div>
              <label className="label">To Date</label>
              <input
                className="input"
                type="date"
                value={toDate}
                min={fromDate}
                max={today()}
                onChange={(event) => setToDate(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="relative mt-6 table-scroll-shell">
          <table className="min-w-[1980px] divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-3 font-semibold">Order</th>
                <th className="px-3 py-3 font-semibold">Type</th>
                <th className="px-3 py-3 font-semibold">Seller</th>
                <th className="px-3 py-3 font-semibold">Progress</th>
                <th className="px-3 py-3 font-semibold">Ordered</th>
                <th className="px-3 py-3 font-semibold">Initial ETA</th>
                <th className="px-3 py-3 font-semibold">Delivered On</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Follow Up</th>
                <th className="px-3 py-3 font-semibold">Revised ETA</th>
                <th className="px-3 py-3 font-semibold">Notes</th>
                <th className="px-3 py-3 font-semibold">Ordered File</th>
                <th className="px-3 py-3 font-semibold">Delivered File</th>
                {canManageLogisticsOrders ? <th className="px-3 py-3 font-semibold">Actions</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((order) => {
                const progress = progressValue(order);
                const progressBarBackground = progressGradient(progress);
                const isSaving = savingOrderId === order.id;
                const changeMeta = order.changeMeta || {};
                const deliveryControl = getDeliveryControl(order);
                const deliveredLocked = isDeliveredLocked(order);
                const unlockRequested = deliveredLocked && deliveryControl.unlockRequested;
                const canRequestUnlock = canManageLogisticsOrders && deliveredLocked && !unlockRequested && !isAdmin();
                const canApproveUnlock = canManageLogisticsOrders && deliveredLocked && unlockRequested && isAdmin();
                const mismatchCount = getMismatchCount(order);
                const deliveredDataAvailable = Boolean(order.deliveredSheetFileName) || hasDeliveredLineData(order);

                return (
                  <tr key={order.id} className="align-top">
                    <td className="px-3 py-4">
                      <div className="font-semibold text-gray-900">{order.orderNumber}</div>
                      {order.lineItems?.length ? (
                        <div className="mt-2 text-xs text-primary-700">
                          {order.lineItems.length} uploaded product line{order.lineItems.length === 1 ? '' : 's'}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-4">
                      <span className={`badge ${orderTypeBadgeClass(order.type)}`}>
                        {orderTypeLabel(order.type)}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <div className="font-medium text-gray-900">{order.seller}</div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="w-48">
                        <div className="h-3 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{ width: `${progress}%`, background: progressBarBackground }}
                          />
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                          <span>{progress}%</span>
                          <span>{remaining(currentEta(order))}</span>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className={`badge ${badgeClass(order.status)}`}>{statusLabel(order.status)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-gray-700">
                      <div>{formatDate(order.orderedDate)}</div>
                      <div className="mt-1 text-xs text-gray-500">Created at {formatTime(order.createdAt)}</div>
                    </td>
                    <td className="px-3 py-4 text-gray-700">{formatDate(order.expectedDelivery)}</td>
                    <td className="px-3 py-4 text-gray-700">
                      {order.deliveredAt ? formatDate(order.deliveredAt) : '-'}
                    </td>
                    <td className="px-3 py-4">
                      <div className="mt-2 min-w-[150px]">
                        <FilterSelect
                          value={order.status}
                          options={STATUSES.map(([value, label]) => ({ value, label }))}
                          disabled={isSaving || deliveredLocked || !canManageLogisticsOrders}
                          onChange={(value) => handleStatusChange(order, value)}
                        />
                      </div>
                      {deliveredLocked ? (
                        <div className="mt-2 space-y-2 text-xs">
                          {!unlockRequested ? (
                            <div className="text-amber-700">
                              Delivered status is locked. Request admin unlock to change it.
                            </div>
                          ) : (
                            <div className="text-blue-700">
                              Unlock requested by {deliveryControl.unlockRequestedByName || 'staff'}.
                            </div>
                          )}
                          {canRequestUnlock ? (
                            <button
                              type="button"
                              className="btn-secondary w-full"
                              disabled={isSaving}
                              onClick={() => handleUnlockRequest(order)}
                            >
                              Request Unlock
                            </button>
                          ) : null}
                          {canApproveUnlock ? (
                            <button
                              type="button"
                              className="btn-primary w-full"
                              disabled={isSaving}
                              onClick={() => handleUnlockApproval(order)}
                            >
                              Approve Unlock
                            </button>
                          ) : null}
                        </div>
                      ) : order.status === 'delivered' && deliveryControl.unlockedAt ? (
                        <div className="mt-2 text-xs text-green-700">
                          Unlocked by {deliveryControl.unlockedByName || 'admin'}.
                        </div>
                      ) : null}
                      {renderChangedBy(changeMeta.status)}
                    </td>
                    <td className="px-3 py-4">
                      <FilterSelect
                        value={order.followUpStatus}
                        className="min-w-[180px]"
                        options={FOLLOW_UPS.map(([value, label]) => ({ value, label }))}
                        disabled={isSaving || !canManageLogisticsOrders}
                        onChange={(value) => handleFollowUpChange(order, value)}
                      />
                      {renderChangedBy(changeMeta.followUpStatus)}
                    </td>
                    <td className="px-3 py-4">
                      <input
                        className="input min-w-[180px]"
                        type="date"
                        value={order.revisedDelivery || ''}
                        disabled={isSaving || order.status === 'cancelled' || !canManageLogisticsOrders}
                        onChange={(event) => handleRevisedEtaChange(order, event.target.value)}
                        onBlur={() => handleRevisedEtaSave(order)}
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        Active ETA: {formatDate(currentEta(order))}
                      </div>
                      {order.revisedDelivery ? (
                        <div className="mt-1 text-xs text-warning-700">Revised from original ETA</div>
                      ) : null}
                      {renderChangedBy(changeMeta.revisedDelivery)}
                    </td>
                    <td className="px-3 py-4">
                      <textarea
                        className="input min-h-[92px] min-w-[340px] resize-y py-3"
                        placeholder="Add seller updates, escalation notes, or receiving notes..."
                        value={order.note || ''}
                        disabled={isSaving || !canManageLogisticsOrders}
                        onChange={(event) => handleNoteChange(order, event.target.value)}
                        onBlur={() => handleNoteSave(order)}
                      />
                      {isSaving ? (
                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Saving...
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-gray-400">
                          {followUpLabel(order.followUpStatus)}
                        </div>
                      )}
                      {!isSaving ? renderChangedBy(changeMeta.note) : null}
                    </td>
                    <td className="px-3 py-4">
                      <div className="min-w-[200px] space-y-3">
                        {order.orderedSheetFileName || order.lineItems?.length ? (
                          <button
                            type="button"
                            className="w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3 text-left text-xs transition hover:bg-slate-100"
                            onClick={() => setSheetModalOrder(order)}
                          >
                            <div className="flex items-center gap-2">
                              <FileSpreadsheet className="h-4 w-4 text-slate-600" />
                              <span className="font-semibold text-slate-800">
                                {order.orderedSheetFileName || 'Order Sheet'}
                              </span>
                            </div>
                            {order.lineItems?.length ? (
                              <p className="mt-1 text-gray-500">{order.lineItems.length} product line{order.lineItems.length === 1 ? '' : 's'}</p>
                            ) : null}
                          </button>
                        ) : null}

                        {canManageLogisticsOrders ? (
                          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-100">
                            <FileSpreadsheet className="h-4 w-4" />
                            <span>{order.lineItems?.some((item) => (item.orderQuantity ?? 0) > 0) ? 'Replace' : 'Upload'}</span>
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              className="hidden"
                              disabled={isSaving}
                              onChange={(event) => handleOrderedFileUploadForOrder(order, event)}
                            />
                          </label>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="min-w-[200px] space-y-3">
                        {deliveredDataAvailable ? (
                          <button
                            type="button"
                            className={`w-full rounded-xl border p-3 text-left text-xs transition ${
                              mismatchCount
                                ? 'border-danger-200 bg-danger-50 text-danger-900 hover:bg-danger-100'
                                : 'border-success-200 bg-success-50 text-success-900 hover:bg-success-100'
                            }`}
                            onClick={() => setSheetModalOrder(order)}
                          >
                            <p className="font-semibold">
                              {order.deliveredSheetFileName || 'Delivered quantities loaded'}
                            </p>
                            <p className="mt-1">
                              {order.deliveredSheetUploadedAt ? `Uploaded ${formatDate(order.deliveredSheetUploadedAt)}` : 'Open comparison'}
                            </p>
                            <div className="mt-2 inline-flex rounded-full bg-white/80 px-2 py-1 font-semibold">
                              {mismatchCount} mismatch{mismatchCount === 1 ? '' : 'es'}
                            </div>
                          </button>
                        ) : (
                          <p className="text-xs text-gray-400">No delivered file</p>
                        )}

                        {canManageLogisticsOrders && order.status === 'delivered' ? (
                          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-primary-300 bg-primary-50 px-3 py-2 text-xs font-medium text-primary-700 transition hover:bg-primary-100">
                            <FileSpreadsheet className="h-4 w-4" />
                            <span>{order.deliveredSheetFileName ? 'Replace' : 'Upload'}</span>
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              className="hidden"
                              disabled={isSaving}
                              onChange={(event) => handleDeliveredFileUpload(order, event)}
                            />
                          </label>
                        ) : null}
                      </div>
                    </td>
                    {canManageLogisticsOrders ? (
                      <td className="px-3 py-4">
                        {order.status !== 'delivered' ? (
                          <button
                            type="button"
                            className="btn-danger text-xs"
                            disabled={isSaving}
                            onClick={() => handleDeleteOrder(order)}
                          >
                            Delete
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Locked</span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!loadingOrders && orders.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
              No logistics orders found in the selected date range.
            </div>
          ) : null}
        </div>
      </div>

      {sheetModalOrder ? (
        <OrderSheetModal
          order={sheetModalOrder}
          canEdit={canManageLogisticsOrders}
          onClose={() => setSheetModalOrder(null)}
          onSave={async (orderId, lineItems) => {
            const target = orders.find((o) => o.id === orderId);
            if (!target) return;
            const response = await logisticsOrdersAPI.update(orderId, {
              ...target,
              lineItems,
            });
            replaceOrder(response.data?.data || { ...target, lineItems });
          }}
          onReupload={handleOrderedFileUploadForOrder}
          onDeliveredUpload={handleDeliveredFileUpload}
        />
      ) : null}
    </div>
  );
};

export default AssistantManagerTasks;
